import { SelectedGender } from "../models/SelectedGender";
import Line from "./Line";
import Point from "./point";
import { MapPoint } from "./MapPoint";

/**
 * Filtre le jeu de données selon les filtres sélectionnés pour le graphe
 * 
 * @param lines Jeu de données complet
 * @param selectedGender Genre sélectionné
 * @param age Age sélectionné
 * @param dateInterval Années sélectionnées
 * @returns Le jeu de données filtré et transformés en coordonnées X et Y
 */
function filterData(lines: Line[], selectedGender: SelectedGender, age: number, dateInterval: string[]): Point[] {
  return lines
    .filter((line: Line) => 
        dateInterval.includes(line.annee) && 
        line.age === age.toString() &&
        line.sexe === getSexAsString(selectedGender)
    )
    .map((line: Line): Point => new Point(parseFloat(line.annee),parseFloat(line.esperance_de_vie)));
}

/**
 * Filtre et compile le jeu de données filtré selon les critères pour les cartes
 * @param mapPoints Jeu de données complet
 * @param selectedGender Genre sélectionné
 * @param age Age sélectionné
 * @param year Année sélectionnée
 * @returns Le jeu de données filtré et compilé (rang, écart H/F)
 */
function filterMapPointsData(mapPoints: MapPoint[], selectedGender: SelectedGender, age: number, year: number) {

  if (selectedGender === SelectedGender.male_women_diff) {
    // Cas de l'écart H/F

    // Données Femmes
    const womenList = mapPoints.filter((mapPoint: MapPoint) => 
        mapPoint.annee === year.toString() && 
        mapPoint.age === age.toString() &&
        mapPoint.sexe === getSexAsString(SelectedGender.women)
    );

    // Données Hommes
    const maleList = mapPoints.filter((mapPoint: MapPoint) => 
        mapPoint.annee === year.toString() && 
        mapPoint.age === age.toString() &&
        mapPoint.sexe === getSexAsString(SelectedGender.male)
    );

    // Données d'EV H et F par noeud (département ou pays)
    const nodeValues: { 
      [key: string]: {
        male?: MapPoint;
        female?: MapPoint;
      }
    } = {};

    // Construction du jeu de données consolidé partir des données Femmes
    womenList.forEach(item => {
      if (!nodeValues[item.node]) {
        nodeValues[item.node] = {};
      }
      nodeValues[item.node].female = item;
    });

    // Complétion du jeu de données consolidé partir des données Hommes
    maleList.forEach(item => {
      if (!nodeValues[item.node]) {
        nodeValues[item.node] = {};
      }
      nodeValues[item.node].male = item;
    });

    // Compilation des données avec le calcul de l'écart
    const result: MapPoint[] = Object.values(nodeValues).map((item) => ({
      age: item.female ? item.female.age : item.male?.age || "",
      annee: item.female ? item.female.annee : item.male?.annee || "",
      esperance_de_vie: 
        item.female?.esperance_de_vie && item.male?.esperance_de_vie 
        ? (parseFloat(item.female.esperance_de_vie) - parseFloat(item.male.esperance_de_vie)).toString()
        : 'nd',
      sexe: 'E',
      node: item.female ? item.female.node : item.male?.node || "",
      rang: -1
    }));

    // Calcul du rang et retour
    computeRang(result);
    return result;
  } else {
    // Filtre H ou filtre F

    // Filtre selon les critères
    const result = mapPoints.filter((mapPoint: MapPoint) => 
        mapPoint.annee === year.toString() && 
        mapPoint.age === age.toString() &&
        mapPoint.sexe === getSexAsString(selectedGender)
    );

    // Calcul du rang et retour
    computeRang(result);
    return result;
  }
}

/**
 * Ordonne la liste des points et 
 * renseigne le rang en place des items ayant une donnée d'espérance de vie
 * @param countries La liste à ordonner
 */
function computeRang(items: MapPoint[]): void{
  const itemsWithData = items
    // On trie les items selon leur espérance de vie
    .sort((a: MapPoint, b:MapPoint) => {
      const EVa = parseFloat(a.esperance_de_vie);
      const EVb = parseFloat(b.esperance_de_vie);

      if (Number.isNaN(EVa) && Number.isNaN(EVb)) {
        return 0;
      }
      if (Number.isNaN(EVa)) {
        return -1;
      }
      if (Number.isNaN(EVb)) {
        return 1;
      }

      return EVa - EVb;
    })
    // On les trie dans l'ordre décroissant
    .reverse()
    // On filtre les items dont l'espérance de vie est définie
    .filter((item: MapPoint) => !Number.isNaN(parseFloat(item.esperance_de_vie)));

  // Pour tous les items ayant une donnée, on leur affecte leur rang
  itemsWithData.forEach((item: MapPoint, i: number) => {
    item.rang = i + 1;
  });
}

/**
 * Renvoie la date la plus haute du jeu de données
 * @param mapPoints Jeu de données
 * @returns La date maximale
 */
function getMaxDate(mapPoints: Line[] | MapPoint[]): number {
  return mapPoints
    .map(mapPoint => parseInt(mapPoint.annee))
    .reduce((previousAnnee, currentAnnee) => Math.max(previousAnnee, currentAnnee));
}

/**
 * Mappe l'enum du genre sur la chaîne correspondante dans le fichier de données
 * @param gender le genre (enum)
 * @returns la chaîne correspondante à cette valeur d'enum dans le jeu de données
 */
function getSexAsString(gender: SelectedGender) {
  switch(gender) {
    case SelectedGender.male:
      return 'H';
    case SelectedGender.women:
      return "F";
  }
}

/**
 * Génère un array d'entiers stringifiés à partir des bornes min et max
 * @param interval un tableau avec au moins deux éléments : les bornes min et max (peu importe l'ordre)
 * @returns un tableau avec ces entiers 
 */
function generateDateArray(interval: number[]): string[] {
  const result: string[] = [];
  const min = Math.min(interval[0], interval[1]);
  const max = Math.max(interval[0], interval[1]);
  const diff = max - min;

  for(let i = 0; i <= diff; i++) {
    result.push((min + i).toString());
  }

  return result;
}

export {filterData, generateDateArray, filterMapPointsData, getMaxDate}
